import React, { useState, useEffect } from 'react';
import { useAuth, useLanguage, api } from '../context/AppContext';
import { Layout } from '../components/Layout';
import FatSecretTrustStat from '../components/FatSecretTrustStat';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Loader2, Save, User, Target, Activity, Bell, Clock, Flame, Zap, Leaf, Users, X, Plus, Heart, Edit3, Trash2, AlertTriangle, Calculator, Scale, Ruler, Globe, RefreshCw, Mic, Shield, CalendarDays, Send, CheckCircle, XCircle, MessageSquare, Crown, MapPin, Sliders, FileText, ExternalLink, Briefcase } from 'lucide-react';
import { LEGAL_DOC_URL, LEGAL_CONSENT_VERSION } from '../constants/legal';
import { needsComplianceNotice } from '../utils/localeUnits';
import { toast } from 'sonner';
import { PROFILE_DEFINITIONS } from '../utils/themeManager';
import ProfileTransition from '../components/ProfileTransition';
import CommTestPanel from '../components/CommTestPanel';

// 🛡️ Defensive number coercion — see DashboardAdvancedStats.js comment.
const safeNum = (v) => {
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};
import FeatureHint from '../components/FeatureHint';
import { useFeatureDiscovery } from '../context/FeatureDiscoveryContext';
import { Progress } from '../components/ui/progress';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  isPushSupported,
  getNotificationPermission,
  subscribeToPush,
  unsubscribeFromPush,
  isSubscribed,
  getNotificationSettings,
  updateNotificationSettings,
  registerServiceWorker
} from '../utils/pushNotifications';
import { 
  UNIT_SYSTEMS, 
  kgToLbs, 
  lbsToKg, 
  cmToFeetInches, 
  feetInchesToCm 
} from '../utils/unitConversions';
import SmartNotificationPreferences from '../components/NotificationPreferences';
import SettingsCalendar from '../components/settings/SettingsCalendar';
import SettingsHormonalHealth from '../components/settings/SettingsHormonalHealth';
import SettingsFamilyProfile from '../components/settings/SettingsFamilyProfile';
import SettingsInjuryMood from '../components/settings/SettingsInjuryMood';
import RecoveryJournal from '../components/RecoveryJournal';
import SettingsDataManagement from '../components/settings/SettingsDataManagement';
import ClinicalModeSettings from '../components/settings/ClinicalModeSettings';
import SettingsNotifications from '../components/settings/SettingsNotifications';
import SettingsSilenceRadio from '../components/settings/SettingsSilenceRadio';
import SettingsMissionPrincipale from '../components/settings/SettingsMissionPrincipale';
import SettingsCoachVoice from '../components/settings/SettingsCoachVoice';
import SettingsSounds from '../components/SettingsSounds';
import CoachYumixIntroModal, { resetCoachYumixIntro } from '../components/CoachYumixIntroModal';
import SettingsAIIntervention from '../components/settings/SettingsAIIntervention';
import SettingsTwoPassMeal from '../components/settings/SettingsTwoPassMeal';
import SettingsRestTimerSound from '../components/settings/SettingsRestTimerSound';
import SettingsFoodExclusions from '../components/settings/SettingsFoodExclusions';
import SettingsHealthProfile from '../components/settings/SettingsHealthProfile';
import SettingsCommunicationStyle from '../components/settings/SettingsCommunicationStyle';
import SettingsIMCCalculator, { JOB_PROFILES } from '../components/settings/SettingsIMCCalculator';
import SettingsMacroTargets from '../components/settings/SettingsMacroTargets';
import SettingsDeficitSelector from '../components/settings/SettingsDeficitSelector';
import SettingsDietaryApproach from '../components/settings/SettingsDietaryApproach';

import AvatarUpload from '../components/AvatarUpload';
import YumixTip from '../components/YumixTip';
import { useCoachTip } from '../constants/coachTips';

export const Settings = () => {
  const { user, updateUser } = useAuth();
  const { t, language, changeLanguage } = useLanguage();
  const { resetAll: resetFeatureHints } = useFeatureDiscovery();
  const [loading, setLoading] = useState(false);
  // Iter. 406.fr.11 — Replay Coach YUMIX intro cinematic
  const [replayCoachIntro, setReplayCoachIntro] = useState(false);
  const neatSettingsTip = useCoachTip('macros.neat.settings');

  // Assistant IA deep-link: /settings?tab=profile|diet|allergies|clinical|notifications
  // Scroll to the matching section ~800ms after mount (wait for lazy sub-components).
  useEffect(() => {
    const tab = new URLSearchParams(window.location.search).get('tab');
    if (!tab) return;
    const validTabs = ['profile', 'diet', 'allergies', 'clinical', 'notifications'];
    if (!validTabs.includes(tab)) return;
    const timer = setTimeout(() => {
      const el = document.getElementById(`settings-tab-${tab}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        el.classList.add('ring-2', 'ring-primary', 'ring-offset-2', 'ring-offset-background');
        setTimeout(() => el.classList.remove('ring-2', 'ring-primary', 'ring-offset-2', 'ring-offset-background'), 2500);
      }
      // Clean URL
      const next = new URLSearchParams(window.location.search);
      next.delete('tab');
      const newSearch = next.toString();
      window.history.replaceState({}, '', `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`);
    }, 900);
    return () => clearTimeout(timer);
  }, []);
  
  // Data management, calendar, hormonal, family, injury, mood states moved to sub-components
  
  // Unit system state
  const [unitSystem, setUnitSystem] = useState(user?.unit_system || 'metric');
  
  // Calendar, hormonal, family, injury, mood states moved to sub-components
  
  // Convert stored values to display values based on unit system
  const getDisplayWeight = (weightLbs) => {
    if (!weightLbs) return '';
    if (unitSystem === 'imperial') return weightLbs.toFixed(1);
    return (weightLbs / 2.20462).toFixed(1); // Convert to kg
  };
  
  const getDisplayHeight = () => {
    if (!user?.height_cm) return { cm: '', feet: '', inches: '' };
    if (unitSystem === 'imperial') {
      const { feet, inches } = cmToFeetInches(user.height_cm);
      return { cm: user.height_cm, feet: feet.toString(), inches: inches.toString() };
    }
    return { cm: user.height_cm.toString(), feet: '', inches: '' };
  };
  
  const [formData, setFormData] = useState({
    name: user?.name || '',
    language: user?.language || language,
    current_weight: getDisplayWeight(user?.current_weight_lbs),
    target_weight: getDisplayWeight(user?.target_weight_lbs),
    height_cm: user?.height_cm?.toString() || '',
    height_feet: '',
    height_inches: '',
    age: user?.age || '',
    sex: user?.sex || '',
    activity_level: user?.activity_level || '',
    job_activity_level: user?.job_activity_level || 'desk',
    goal: user?.goal || 'maintain',
    postal_code: user?.postal_code || '',
    booking_link: user?.booking_link || '',
  });
  
  // Initialize height for imperial
  useEffect(() => {
    if (unitSystem === 'imperial' && user?.height_cm) {
      const { feet, inches } = cmToFeetInches(user.height_cm);
      setFormData(prev => ({
        ...prev,
        height_feet: feet.toString(),
        height_inches: inches.toString(),
      }));
    }
  }, [unitSystem, user?.height_cm]);

  // IMC Calculator state
  const [showIMCCalculator, setShowIMCCalculator] = useState(false);
  const [imcData, setImcData] = useState({
    weight_kg: user?.current_weight_lbs ? (user.current_weight_lbs / 2.20462).toFixed(1) : '',
    height_cm: user?.height_cm || '',
    age: user?.age || '',
    sex: user?.sex || '',
    activity_level: user?.activity_level || '',
    goal: user?.goal || 'maintain',
  });
  const [calculatedIMC, setCalculatedIMC] = useState(null);

  // Profile change state
  const [showProfileTransition, setShowProfileTransition] = useState(false);
  const [pendingProfile, setPendingProfile] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);

  // Food exclusions state (for AI coach)
  const [foodExclusions, setFoodExclusions] = useState(user?.food_exclusions || []);
  const [exclusionInput, setExclusionInput] = useState('');
  const [exclusionSaving, setExclusionSaving] = useState(false);

  // Family, hormonal, injury, mood states moved to sub-components

  const addExclusion = () => {
    const val = exclusionInput.trim();
    if (val && !foodExclusions.includes(val)) {
      setFoodExclusions(prev => [...prev, val]);
      setExclusionInput('');
    }
  };

  const removeExclusion = (item) => {
    setFoodExclusions(prev => prev.filter(x => x !== item));
  };

  const saveExclusions = async () => {
    setExclusionSaving(true);
    try {
      await api.put('/users/profile', { food_exclusions: foodExclusions });
      updateUser({ ...user, food_exclusions: foodExclusions });
      toast.success(t('settings.toast_exclusions_updated'));
    } catch (error) {
      toast.error(t('toast.save_error'));
    } finally {
      setExclusionSaving(false);
    }
  };

  // Macro targets state
  const [macrosForm, setMacrosForm] = useState({
    daily_calories: 2000,
    protein_min_g: 100,
    protein_max_g: 180,
    carbs_target_g: 250,
    fats_target_g: 70,
    fiber_target_g: 35
  });
  const [macrosLoading, setMacrosLoading] = useState(false);
  const [macrosSaving, setMacrosSaving] = useState(false);
  const [macrosCustomByCoach, setMacrosCustomByCoach] = useState(false);

  // Notification states
  const [notifSupported, setNotifSupported] = useState(false);
  const [notifPermission, setNotifPermission] = useState('default');
  const [notifSubscribed, setNotifSubscribed] = useState(false);
  const [notifSettings, setNotifSettings] = useState({
    enabled: true,
    breakfast_time: '08:00',
    lunch_time: '12:00',
    dinner_time: '19:00',
    weekly_summary: true
  });
  const [notifLoading, setNotifLoading] = useState(false);

  // Session info state
  const [sessionInfo, setSessionInfo] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(false);

  // IMC Calculator constants
  const ACTIVITY_LEVELS = {
    fr: [
      { value: 'sedentary', label: 'Sedentaire (bureau)', factor: 1.2 },
      { value: 'light', label: 'Leger (1-3x/sem)', factor: 1.375 },
      { value: 'moderate', label: 'Modere (3-5x/sem)', factor: 1.55 },
      { value: 'intense', label: 'Intense (6-7x/sem)', factor: 1.725 },
      { value: 'elite', label: 'Elite (2x/jour)', factor: 1.9 },
      { value: 'athlete', label: 'Athlète', factor: 1.9 },
    ],
    en: [
      { value: 'sedentary', label: 'Sedentary (desk job)', factor: 1.2 },
      { value: 'light', label: 'Light (1-3x/week)', factor: 1.375 },
      { value: 'moderate', label: 'Moderate (3-5x/week)', factor: 1.55 },
      { value: 'intense', label: 'Intense (6-7x/week)', factor: 1.725 },
      { value: 'elite', label: 'Elite (2x/day)', factor: 1.9 },
      { value: 'athlete', label: 'Athlete', factor: 1.9 },
    ]
  };

  const IMC_CATEGORIES = {
    fr: [
      { max: 18.5, label: 'Insuffisance pondérale', color: 'text-blue-400', bg: 'bg-blue-500/20' },
      { max: 24.9, label: 'Poids normal', color: 'text-green-400', bg: 'bg-green-500/20' },
      { max: 29.9, label: 'Surpoids', color: 'text-yellow-400', bg: 'bg-yellow-500/20' },
      { max: 34.9, label: 'Obésité classe 1', color: 'text-orange-400', bg: 'bg-orange-500/20' },
      { max: Infinity, label: 'Obésité sévère', color: 'text-red-500', bg: 'bg-red-500/20' },
    ],
    en: [
      { max: 18.5, label: 'Underweight', color: 'text-blue-400', bg: 'bg-blue-500/20' },
      { max: 24.9, label: 'Normal weight', color: 'text-green-400', bg: 'bg-green-500/20' },
      { max: 29.9, label: 'Overweight', color: 'text-yellow-400', bg: 'bg-yellow-500/20' },
      { max: 34.9, label: 'Obesity class 1', color: 'text-orange-400', bg: 'bg-orange-500/20' },
      { max: Infinity, label: 'Severe obesity', color: 'text-red-500', bg: 'bg-red-500/20' },
    ]
  };

  const calculateIMCAndMacros = () => {
    const weightKg = parseFloat(imcData.weight_kg);
    const heightCm = parseFloat(imcData.height_cm);
    const age = parseInt(imcData.age);
    const sex = imcData.sex;
    const activityLevel = imcData.activity_level;
    const goal = imcData.goal;

    if (!weightKg || !heightCm || !age || !sex || !activityLevel) return null;

    // IMC
    const heightM = heightCm / 100;
    const imc = weightKg / (heightM * heightM);
    const categories = IMC_CATEGORIES[language] || IMC_CATEGORIES.fr;
    const imcCategory = categories.find(cat => imc <= cat.max);

    // BMR - Mifflin-St Jeor
    let bmr;
    if (sex === 'male') {
      bmr = (10 * weightKg) + (6.25 * heightCm) - (5 * age) + 5;
    } else {
      bmr = (10 * weightKg) + (6.25 * heightCm) - (5 * age) - 161;
    }

    // TDEE
    const activities = ACTIVITY_LEVELS[language] || ACTIVITY_LEVELS.fr;
    const activity = activities.find(a => a.value === activityLevel);
    const tdee = bmr * (activity?.factor || 1.2) * 0.90; // -10% correction factor (Coach Yuri)

    // Adjust for goal
    let calorieAdjust = 0;
    if (goal === 'lose') calorieAdjust = -500;
    if (goal === 'gain') calorieAdjust = 300;
    const targetCalories = Math.round(tdee + calorieAdjust);

    // Profile-based macros
    const activeProfile = user?.active_profile || 'pilote';
    
    // MANUAL COACH MODE: Skip calculations entirely
    if (activeProfile === 'manual_coach') {
      return {
        imc: Math.round(imc * 10) / 10,
        imcCategory,
        bmr: Math.round(bmr),
        tdee: Math.round(tdee),
        targetCalories: macrosForm.daily_calories || Math.round(tdee),
        protein_min_g: macrosForm.protein_min_g || 150,
        protein_max_g: macrosForm.protein_max_g || 200,
        carbs_target_g: macrosForm.carbs_target_g || 250,
        fats_target_g: macrosForm.fats_target_g || 70,
        fiber_target_g: macrosForm.fiber_target_g || 35,
        profile: 'manual_coach',
        manual_mode: true,
      };
    }
    
    let proteinPctMin, proteinPctMax, carbsPct, fatPctMin, fatPctMax, fiberBase;

    switch (activeProfile) {
      case 'pilote':
        proteinPctMin = 0.35; proteinPctMax = 0.40; carbsPct = 0.35;
        fatPctMin = 0.20; fatPctMax = 0.25; fiberBase = 40; break;
      case 'deesse':
        proteinPctMin = 0.30; proteinPctMax = 0.30; carbsPct = 0.40;
        fatPctMin = 0.25; fatPctMax = 0.30; fiberBase = 35; break;
      case 'espoir':
        proteinPctMin = 0.25; proteinPctMax = 0.30; carbsPct = 0.45;
        fatPctMin = 0.25; fatPctMax = 0.30; fiberBase = 35; break;
      case 'sagesse':
        proteinPctMin = 0.30; proteinPctMax = 0.30; carbsPct = 0.40;
        fatPctMin = 0.30; fatPctMax = 0.30; fiberBase = 45; break;
      default:
        proteinPctMin = 0.30; proteinPctMax = 0.35; carbsPct = 0.40;
        fatPctMin = 0.25; fatPctMax = 0.30; fiberBase = 35;
    }

    // Calculate grams from profile percentages
    const proteinMinG = Math.round((targetCalories * proteinPctMin) / 4);
    const proteinMaxG = Math.round((targetCalories * proteinPctMax) / 4);

    // Per-kg floor for PILOTE (2.2g/kg) and SAGESSE (1.5g/kg)
    const piloteFloor = activeProfile === 'pilote' ? Math.round(weightKg * 2.2) : 0;
    const sagesseFloor = activeProfile === 'sagesse' ? Math.round(weightKg * 1.5) : 0;
    const finalProteinMin = Math.max(proteinMinG, piloteFloor, sagesseFloor);
    const finalProteinMax = Math.max(proteinMaxG, piloteFloor, sagesseFloor);

    const carbG = Math.round((targetCalories * carbsPct) / 4);
    const fatG = Math.round((targetCalories * ((fatPctMin + fatPctMax) / 2)) / 9);
    const fiberG = fiberBase;

    return {
      imc: Math.round(imc * 10) / 10,
      imcCategory,
      bmr: Math.round(bmr),
      tdee: Math.round(tdee),
      targetCalories,
      protein_min_g: finalProteinMin,
      protein_max_g: finalProteinMax,
      carbs_target_g: carbG,
      fats_target_g: fatG,
      fiber_target_g: fiberG,
      profile: activeProfile,
    };
  };

  const applyIMCCalculation = async () => {
    const result = calculateIMCAndMacros();
    if (!result) return;

    setCalculatedIMC(result);
    
    // Update macros form
    setMacrosForm({
      daily_calories: result.targetCalories,
      protein_min_g: result.protein_min_g,
      protein_max_g: result.protein_max_g,
      carbs_target_g: result.carbs_target_g,
      fats_target_g: result.fats_target_g,
      fiber_target_g: result.fiber_target_g,
    });

    // Update profile form
    const weightLbs = parseFloat(imcData.weight_kg) * 2.20462;
    setFormData(prev => ({
      ...prev,
      current_weight_lbs: weightLbs.toFixed(1),
      height_cm: imcData.height_cm,
      age: imcData.age,
      sex: imcData.sex,
      activity_level: imcData.activity_level,
      goal: imcData.goal,
    }));

    toast.success(t('settings_page.macros_calcules_cliquez_sur_sauvegarder_', 'Macros calculés! Cliquez sur Sauvegarder pour appliquer.'));
  };

  useEffect(() => {
    // Load macro targets
    const loadMacros = async () => {
      setMacrosLoading(true);
      try {
        const response = await api.get('/users/macros');
        setMacrosForm({
          daily_calories: response.data.daily_calories || 2000,
          protein_min_g: response.data.protein_min_g || 100,
          protein_max_g: response.data.protein_max_g || 180,
          carbs_target_g: response.data.carbs_target_g || 250,
          fats_target_g: response.data.fats_target_g || 70,
          fiber_target_g: response.data.fiber_target_g || 35
        });
        setMacrosCustomByCoach(response.data.macros_custom_set_by_coach || false);
      } catch (error) {
        console.error('Error loading macros:', error);
      } finally {
        setMacrosLoading(false);
      }
    };
    loadMacros();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- Mount-only effect

  useEffect(() => {
    // Check notification support and status
    const checkNotifications = async () => {
      const supported = isPushSupported();
      setNotifSupported(supported);
      
      if (supported) {
        setNotifPermission(getNotificationPermission());
        const subscribed = await isSubscribed();
        setNotifSubscribed(subscribed);
        
        // Register service worker
        await registerServiceWorker();
        
        // Load notification settings
        try {
          const settings = await getNotificationSettings();
          setNotifSettings(settings);
        } catch (error) {
          console.error('Error loading notification settings:', error);
        }
      }
    };
    
    checkNotifications();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- Mount-only effect

  // Load session info
  useEffect(() => {
    const loadSessionInfo = async () => {
      setSessionLoading(true);
      try {
        const response = await api.get('/auth/session-info');
        setSessionInfo(response.data);
      } catch (error) {
        console.error('Error loading session info:', error);
      } finally {
        setSessionLoading(false);
      }
    };
    
    loadSessionInfo();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Convert weight based on unit system
      let weightLbs, targetWeightLbs, heightCm;
      
      if (unitSystem === 'imperial') {
        // User entered lbs directly
        weightLbs = formData.current_weight ? parseFloat(formData.current_weight) : undefined;
        targetWeightLbs = formData.target_weight ? parseFloat(formData.target_weight) : undefined;
        // Height from feet/inches
        const feet = parseFloat(formData.height_feet) || 0;
        const inches = parseFloat(formData.height_inches) || 0;
        heightCm = feet > 0 ? feetInchesToCm(feet, inches) : undefined;
      } else {
        // User entered kg - convert to lbs for storage
        weightLbs = formData.current_weight ? kgToLbs(parseFloat(formData.current_weight)) : undefined;
        targetWeightLbs = formData.target_weight ? kgToLbs(parseFloat(formData.target_weight)) : undefined;
        heightCm = formData.height_cm ? parseFloat(formData.height_cm) : undefined;
      }
      
      const response = await api.put('/users/profile', {
        name: formData.name,
        language: formData.language,
        current_weight_lbs: weightLbs,
        target_weight_lbs: targetWeightLbs,
        height_cm: heightCm,
        age: formData.age ? parseInt(formData.age) : undefined,
        sex: formData.sex,
        activity_level: formData.activity_level,
        job_activity_level: formData.job_activity_level || 'desk',
        goal: formData.goal,
        unit_system: unitSystem,
        postal_code: formData.postal_code || undefined,
        booking_link: formData.booking_link || undefined,
      });

      updateUser(response.data);
      changeLanguage(formData.language);
      
      toast.success(t('settings.toast_profile_updated'));
    } catch (error) {
      console.error('Update error:', error);
      toast.error(t('settings.toast_update_error'));
    } finally {
      setLoading(false);
    }
  };


  // Profile change handler
  const handleProfileChange = async (profileId) => {
    if (profileId === user?.active_profile) return;
    setSavingProfile(true);
    setPendingProfile(profileId);
    try {
      const response = await api.put('/users/profile', { active_profile: profileId });
      updateUser(response.data);
      setShowProfileTransition(true);
    } catch (error) {
      console.error('Profile change error:', error);
      toast.error(t('settings_page.erreur_de_changement_de_profil', 'Erreur de changement de profil'));
      setPendingProfile(null);
    } finally {
      setSavingProfile(false);
    }
  };

  const handleProfileTransitionComplete = () => {
    setShowProfileTransition(false);
    setPendingProfile(null);
    toast.success(t('settings_page.profil_active', 'Profil activé!'));
  };

  const handleSaveMacros = async () => {
    setMacrosSaving(true);
    try {
      const response = await api.put('/users/macros', {
        daily_calories: parseInt(macrosForm.daily_calories),
        protein_min_g: parseInt(macrosForm.protein_min_g),
        protein_max_g: parseInt(macrosForm.protein_max_g),
        carbs_target_g: parseInt(macrosForm.carbs_target_g),
        fats_target_g: parseInt(macrosForm.fats_target_g),
        fiber_target_g: parseInt(macrosForm.fiber_target_g)
      });
      
      // Update local user context with new values
      if (response.data.user) {
        updateUser(response.data.user);
      }
      
      // Reload macros from API to confirm save
      const macrosResponse = await api.get('/users/macros');
      setMacrosForm({
        daily_calories: macrosResponse.data.daily_calories || 2000,
        protein_min_g: macrosResponse.data.protein_min_g || 100,
        protein_max_g: macrosResponse.data.protein_max_g || 180,
        carbs_target_g: macrosResponse.data.carbs_target_g || 250,
        fats_target_g: macrosResponse.data.fats_target_g || 70,
        fiber_target_g: macrosResponse.data.fiber_target_g || 35
      });
      
      toast.success(t('settings.toast_macros_saved'));
    } catch (error) {
      console.error('Error saving macros:', error);
      toast.error(error.response?.data?.detail || t('toast.save_error'));
    } finally {
      setMacrosSaving(false);
    }
  };

  const isCoachOrAdmin = user?.role === 'coach' || user?.role === 'admin';
  const isSuperAdmin = user?.role === 'super_admin';
  const isPrivileged = isCoachOrAdmin || isSuperAdmin;

  // Fetch client's assigned coach booking link
  const [coachBookingLink, setCoachBookingLink] = useState('');
  const [coachBookingName, setCoachBookingName] = useState('Coach YUMIX');
  useEffect(() => {
    if (isPrivileged) return;
    (async () => {
      try {
        const res = await api.get('/users/my-coach-booking');
        if (res.data?.booking_link) setCoachBookingLink(res.data.booking_link);
        if (res.data?.coach_name) setCoachBookingName(res.data.coach_name);
      } catch { /* */ }
    })();
  }, [isPrivileged]);

  const handleEnableNotifications = async () => {
    setNotifLoading(true);
    try {
      await subscribeToPush();
      setNotifSubscribed(true);
      setNotifPermission('granted');
      toast.success(t('settings_page.notifications_activees', 'Notifications activées!'));
    } catch (error) {
      console.error('Error enabling notifications:', error);
      if (error.message === 'Notification permission denied') {
        toast.error(t('settings_page.permission_refusee_activez_les_notificat', 'Permission refusée. Activez les notifications dans les paramètres du navigateur.'));
      } else {
        toast.error(t('settings_page.erreur_d_activation', 'Erreur d\\\'activation'));
      }
    } finally {
      setNotifLoading(false);
    }
  };

  const handleDisableNotifications = async () => {
    setNotifLoading(true);
    try {
      await unsubscribeFromPush();
      setNotifSubscribed(false);
      toast.success(t('settings_page.notifications_desactivees', 'Notifications désactivées'));
    } catch (error) {
      console.error('Error disabling notifications:', error);
      toast.error(t('settings_page.erreur_de_desactivation', 'Erreur de désactivation'));
    } finally {
      setNotifLoading(false);
    }
  };

  const handleUpdateNotifSettings = async (newSettings) => {
    const updated = { ...notifSettings, ...newSettings };
    setNotifSettings(updated);
    
    try {
      await updateNotificationSettings(updated);
      toast.success(t('settings.toast_settings_updated'));
    } catch (error) {
      console.error('Error updating notification settings:', error);
    }
  };

  // Family, data management, injury, mood, calendar handlers moved to sub-components

  // Macro targets state

  return (
    <Layout>
      <div className="p-4 md:p-8 lg:p-12 max-w-3xl mx-auto" data-testid="settings-page">
        {/* Header */}
        <div
          className="mb-8"
        >
          <h1 className="font-heading text-4xl sm:text-5xl font-black uppercase tracking-tight text-foreground mb-2">
            {t('settings.title')}
          </h1>
          <p className="text-muted-foreground">
            {t('settings.subtitle')}
          </p>
        </div>

        {/* Trust metric — pushes toward precise tracking + Canadian products */}
        <FatSecretTrustStat />

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Combat Profile Section */}
          <Card className="bg-card border-border" data-testid="settings-profile-selector" id="settings-tab-profile">
            <CardHeader>
              <CardTitle className="font-heading text-xl uppercase tracking-tight flex items-center gap-2">
                <Shield className="w-5 h-5 text-primary" />
                {t('settings_page.profil_de_combat', 'Profil de Combat')}
              </CardTitle>
              <CardDescription>
                {t('settings_page.change_ta_phase_de_vie_tes_macros_seront', 'Change ta phase de vie. Tes macros seront ajustés automatiquement.')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3 min-w-0">
                {Object.values(PROFILE_DEFINITIONS).map((p) => {
                  const icons = { Target, Crown, Shield, Flame, Sliders };
                  const Icon = icons[p.icon] || Target;
                  const isActive = user?.active_profile === p.id;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => handleProfileChange(p.id)}
                      disabled={savingProfile}
                      data-testid={`settings-profile-${p.id}`}
                      className={`relative p-4 rounded-xl text-center transition-all duration-300 min-w-0 overflow-hidden ${
                        isActive
                          ? 'border-2 scale-[1.02]'
                          : 'border-2 border-transparent bg-secondary/50 hover:border-muted-foreground/30'
                      }`}
                      style={isActive ? {
                        borderColor: p.colors.primary,
                        background: `linear-gradient(135deg, ${p.colors.primary}15, ${p.colors.accent}15)`,
                        boxShadow: `0 0 20px ${p.colors.primary}20`,
                      } : undefined}
                    >
                      {isActive && (
                        <div className="absolute top-2 right-2">
                          <CheckCircle className="w-4 h-4" style={{ color: p.colors.primary }} />
                        </div>
                      )}
                      <div
                        className="w-12 h-12 mx-auto rounded-full flex items-center justify-center mb-2"
                        style={{ background: isActive ? `linear-gradient(135deg, ${p.colors.primary}, ${p.colors.accent})` : undefined }}
                      >
                        <Icon className={`w-6 h-6 ${isActive ? 'text-white' : 'text-muted-foreground'}`} />
                      </div>
                      <p className="font-heading text-sm uppercase tracking-tight text-foreground">{p.label}</p>
                      <p className="text-[10px] text-muted-foreground mt-1 leading-snug">
                        {t(`onboarding.profile_${p.id}_desc`, p.description_fr)}
                      </p>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Profile Section */}
          <div
          >
            {/* Mission Principale — Flexibilite Tactique */}
            <SettingsMissionPrincipale />
          </div>

          <div
          >
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="font-heading text-xl uppercase tracking-tight flex items-center gap-2">
                  <User className="w-5 h-5 text-primary" />
                  {t('settings.profile')}
                </CardTitle>
                <CardDescription>
                  {t('settings.personal_info')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Avatar Upload */}
                <AvatarUpload />
                <div className="border-b border-border" />
                <div className="space-y-2">
                  <Label htmlFor="name">{t('settings_page.nom_complet', 'Nom complet')}</Label>
                  <Input
                    id="name"
                    data-testid="name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="bg-secondary border-input"
                  />
                </div>

                <div className="flex items-center justify-end">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      resetFeatureHints();
                      toast.success(t('settings_page.astuces_reactivees', 'Astuces réactivées 💡'));
                    }}
                    className="text-xs text-muted-foreground hover:text-primary"
                    data-testid="reset-feature-hints-btn"
                  >
                    💡 {t('settings_page.revoir_les_astuces', 'Revoir les astuces')}
                  </Button>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="language">{t('settings.language')}</Label>
                  <Select
                    value={formData.language}
                    onValueChange={(value) => {
                      // Update local form state AND apply i18n change immediately so the
                      // entire UI re-renders in the picked language without waiting for
                      // the user to press "Save". The Save button still persists the
                      // choice to the user's profile in MongoDB (line 502).
                      setFormData({ ...formData, language: value });
                      changeLanguage(value);
                    }}
                  >
                    <SelectTrigger id="language" data-testid="settings-language-select" className="bg-secondary border-input">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fr" data-testid="settings-language-option-fr">🇫🇷 Français</SelectItem>
                      <SelectItem value="en" data-testid="settings-language-option-en">🇬🇧 English</SelectItem>
                      <SelectItem value="es" data-testid="settings-language-option-es">🇪🇸 Español (beta)</SelectItem>
                    </SelectContent>
                  </Select>
                  {formData.language === 'es' && (
                    <p className="text-xs text-muted-foreground" data-testid="settings-language-es-notice">
                      {t('settings.language_es_notice')}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="unit-system" className="flex items-center gap-2">
                    <Globe className="w-4 h-4 text-primary" />
                    {t('settings_page.systeme_de_mesure', 'Système de mesure')}
                  </Label>
                  <Select
                    value={unitSystem}
                    onValueChange={(value) => {
                      setUnitSystem(value);
                      // Convert existing values to new unit system
                      if (value === 'imperial' && user?.current_weight_lbs) {
                        setFormData(prev => ({
                          ...prev,
                          current_weight: safeNum(user.current_weight_lbs).toFixed(1),
                          target_weight: user.target_weight_lbs ? safeNum(user.target_weight_lbs).toFixed(1) : '',
                        }));
                        if (user?.height_cm) {
                          const { feet, inches } = cmToFeetInches(user.height_cm);
                          setFormData(prev => ({
                            ...prev,
                            height_feet: feet.toString(),
                            height_inches: inches.toString(),
                          }));
                        }
                      } else if (value === 'metric' && user?.current_weight_lbs) {
                        setFormData(prev => ({
                          ...prev,
                          current_weight: lbsToKg(user.current_weight_lbs).toFixed(1),
                          target_weight: user.target_weight_lbs ? lbsToKg(user.target_weight_lbs).toFixed(1) : '',
                          height_cm: user.height_cm?.toString() || '',
                        }));
                      }
                    }}
                  >
                    <SelectTrigger id="unit-system" data-testid="settings-unit-select" className="bg-secondary border-input">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="metric">
                        {t('settings_page.metrique_kg_cm', 'Métrique (kg, cm)')}
                      </SelectItem>
                      <SelectItem value="imperial">
                        {t('settings_page.imperial_lb_pi', 'Impérial (lb, pi)')}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  
                  {/* Real-time unit conversion preview */}
                  {user?.current_weight_lbs && (
                    <div className="mt-3 p-3 bg-secondary/50 border border-border rounded-lg">
                      <p className="text-xs text-muted-foreground mb-2">
                        {t('settings_page.apercu_de_vos_mesures', 'Aperçu de vos mesures :')}
                      </p>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="flex items-center gap-2">
                          <Scale className="w-4 h-4 text-primary" />
                          <div>
                            <span className="text-muted-foreground">{t('settings_page.poids', 'Poids: ')}</span>
                            <span className="font-medium text-foreground">
                              {unitSystem === 'imperial' 
                                ? `${safeNum(user.current_weight_lbs).toFixed(1)} lb`
                                : `${lbsToKg(safeNum(user.current_weight_lbs)).toFixed(1)} kg`
                              }
                            </span>
                          </div>
                        </div>
                        {user?.height_cm && (
                          <div className="flex items-center gap-2">
                            <Ruler className="w-4 h-4 text-primary" />
                            <div>
                              <span className="text-muted-foreground">{t('settings_page.taille', 'Taille: ')}</span>
                              <span className="font-medium text-foreground">
                                {unitSystem === 'imperial' 
                                  ? (() => {
                                      const { feet, inches } = cmToFeetInches(user.height_cm);
                                      return t('settings_page.height_imperial', '{{feet}}pi {{inches}}po', { feet, inches });
                                    })()
                                  : `${Math.round(user.height_cm)} cm`
                                }
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Postal Code / Location */}
                <div className="space-y-2 pt-3 border-t border-border">
                  <Label htmlFor="postal_code" className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-orange-500" />
                    {t('settings_page.code_postal_quebec', 'Code Postal (Quebec)')}
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    {t('settings_page.pour_te_recommander_les_magasins_et_spec', 'Pour te recommander les magasins et speciaux pres de chez toi')}
                  </p>
                  <div className="flex items-center gap-2">
                    <Input
                      id="postal_code"
                      data-testid="postal-code-input"
                      value={formData.postal_code}
                      onChange={(e) => setFormData({ ...formData, postal_code: e.target.value.toUpperCase().slice(0, 7) })}
                      placeholder="H2X 1Y4"
                      className="bg-secondary border-input font-mono w-40"
                      maxLength={7}
                    />
                    <Button
                      type="button"
                      data-testid="save-postal-code-btn"
                      disabled={loading}
                      onClick={async () => {
                        if (!formData.postal_code) return;
                        setLoading(true);
                        try {
                          const res = await api.put('/users/profile', { postal_code: formData.postal_code });
                          updateUser(res.data);
                          localStorage.setItem('postal_code', formData.postal_code);
                          toast.success(t('settings_page.code_postal_sauvegarde', 'Code postal sauvegardé!'));
                        } catch {
                          toast.error(t('settings_page.erreur_de_sauvegarde', 'Erreur de sauvegarde'));
                        } finally {
                          setLoading(false);
                        }
                      }}
                      className="bg-orange-500 hover:bg-orange-600 text-white px-4"
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    </Button>
                  </div>
                  {user?.geo_profile?.region_name && (
                    <p className="text-xs text-emerald-500 flex items-center gap-1" data-testid="geo-region-display">
                      <MapPin className="w-3 h-3" />
                      {user.geo_profile.region_name} — {user.geo_profile.available_stores?.length || 0} {t('settings_page.magasins', 'magasins')}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Notifications Section (extracted) */}
          <div id="settings-tab-notifications"><SettingsNotifications /></div>

          {/* Silence Radio Mode (Anti-Churn) */}
          <SettingsSilenceRadio />

          {/* Coach Voice Preference (extracted) */}
          <SettingsCoachVoice />

          {/* Sound Director — Synthesized milestone sounds (Web Audio API) */}
          <div id="settings-tab-sounds"><SettingsSounds language={language} /></div>

          {/* Replay Coach YUMIX intro cinematic */}
          <div
            className="relative overflow-hidden rounded-2xl border border-orange-400/40 p-4 bg-gradient-to-br from-orange-950/40 via-card/60 to-red-950/30"
            style={{ boxShadow: '0 0 18px rgba(251,146,60,0.25), inset 0 0 0 1px rgba(251,146,60,0.12)' }}
            data-testid="settings-coach-intro-card"
          >
            <div className="flex items-center gap-3">
              <div
                className="w-12 h-12 rounded-full overflow-hidden shrink-0"
                style={{
                  background: 'radial-gradient(circle at 30% 25%, rgba(251,146,60,0.55), rgba(168,85,247,0.25) 70%, transparent 100%)',
                  border: '2px solid rgba(251,191,36,0.65)',
                }}
              >
                <img src="/assets/coach-yumix-avatar-2.png" alt="Coach YUMIX" className="w-full h-full object-cover" style={{ objectPosition: 'center 22%', transform: 'scale(1.12)' }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-[0.28em] font-bold text-orange-300 mb-0.5">
                  {language === 'fr' ? 'Cinématique Coach YUMIX' : 'Coach YUMIX cinematic'}
                </p>
                <p className="text-sm text-foreground/90">
                  {language === 'fr' ? "Revoir l'apparition épique du Coach." : 'Replay the epic Coach arrival.'}
                </p>
              </div>
              <button
                onClick={() => { resetCoachYumixIntro(); setReplayCoachIntro(true); }}
                className="shrink-0 px-3 py-2 rounded-lg bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold text-[11px] uppercase tracking-wider hover:scale-[1.03] active:scale-[0.98] transition-transform"
                style={{ boxShadow: '0 0 14px rgba(251,146,60,0.5)' }}
                data-testid="settings-replay-coach-intro-btn"
              >
                {language === 'fr' ? 'Revoir' : 'Replay'}
              </button>
            </div>
          </div>
          <CoachYumixIntroModal
            open={replayCoachIntro}
            onClose={() => setReplayCoachIntro(false)}
            language={language}
          />


          {/* AI Intervention Level */}
          <SettingsAIIntervention />

          {/* Iter. 411.mm3 — Two-pass meal analysis (opt-in, +2s per scan) */}
          <SettingsTwoPassMeal />

          {/* Iter. 411.mm8 — Rest-timer sound picker */}
          <SettingsRestTimerSound />

          {/* Food Exclusions (extracted) */}
          <div id="settings-tab-allergies"><SettingsFoodExclusions /></div>

          {/* Dietary Approach — Mon Profil Nutritionnel */}
          <div id="settings-tab-diet"><SettingsDietaryApproach /></div>

          {/* Health Profile — Dossier Biomédical Tactique */}
          <div id="settings-tab-clinical"><SettingsHealthProfile /></div>

          {/* Communication Style — Le Caméléon Psychologique */}
          <SettingsCommunicationStyle />

          {/* Hormonal Health Module (extracted) */}
          <SettingsHormonalHealth />

          {/* Cultural / Religious Mode Section */}
          <Card className="bg-card border-border" data-testid="settings-cultural-mode">
            <CardHeader>
              <CardTitle className="font-heading text-xl uppercase tracking-tight flex items-center gap-2">
                <Globe className="w-5 h-5 text-cyan-400" />
                {t('settings_page.mode_culturel_religieux', 'Mode Culturel / Religieux')}
              </CardTitle>
              <CardDescription>
                {t('settings_page.le_coach_ia_adaptera_ses_suggestions_ali', 'Coach YUMIX adaptera ses suggestions alimentaires selon votre pratique')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {[
                  { id: 'standard', icon: '🌍', label: 'Standard', desc_fr: 'Aucune restriction religieuse', desc_en: 'No religious restriction' },
                  { id: 'ramadan', icon: '🌙', label: 'Ramadan', desc_fr: 'Horaires de jeune + iftar adaptes', desc_en: 'Fasting hours + iftar adapted' },
                  { id: 'halal', icon: '☪️', label: 'Halal', desc_fr: 'Filtrage porc, alcool, gelatine', desc_en: 'Filters pork, alcohol, gelatin' },
                  { id: 'kasher', icon: '✡️', label: 'Kasher', desc_fr: 'Separation lait/viande, porc interdit', desc_en: 'Milk/meat separation, no pork' },
                  { id: 'careme', icon: '✝️', label: t('settings_page.careme', 'Careme'), desc_fr: 'Vendredi sans viande', desc_en: 'Meatless Fridays' },
                  { id: 'hindou', icon: '🕉️', label: t('settings_page.hindou', 'Hindou'), desc_fr: 'Vegetarien + pas de boeuf', desc_en: 'Vegetarian + no beef' },
                ].map((mode) => {
                  const isActive = (user?.cultural_mode || 'standard') === mode.id;
                  return (
                    <button
                      key={mode.id}
                      type="button"
                      data-testid={`cultural-mode-${mode.id}`}
                      onClick={async () => {
                        try {
                          const res = await api.put('/users/profile', { cultural_mode: mode.id });
                          updateUser(res.data);
                          toast.success(t('settings_page.mode_activated', 'Mode {{label}} activé !', { label: mode.label }));
                        } catch {
                          toast.error(t('settings_page.erreur_de_sauvegarde', 'Erreur de sauvegarde'));
                        }
                      }}
                      className={`relative p-4 rounded-xl text-center transition-all duration-200 border-2 ${
                        isActive
                          ? 'border-cyan-500 bg-cyan-500/10 shadow-lg shadow-cyan-500/10'
                          : 'border-transparent bg-secondary/50 hover:border-muted-foreground/30'
                      }`}
                    >
                      {isActive && (
                        <div className="absolute top-2 right-2">
                          <CheckCircle className="w-4 h-4 text-cyan-400" />
                        </div>
                      )}
                      <span className="text-2xl block mb-1">{mode.icon}</span>
                      <p className="font-heading text-sm uppercase tracking-tight text-foreground">{mode.label}</p>
                      <p className="text-[10px] text-muted-foreground mt-1 leading-snug break-words">
                        {t(`settings_page.cultural_${mode.id}_desc`, mode.desc_fr)}
                      </p>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Family Profile Section (extracted) */}
          <SettingsFamilyProfile />

          {/* Goals Section */}
          {(
            <div
            >
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="font-heading text-xl uppercase tracking-tight flex items-center gap-2">
                    <Target className="w-5 h-5 text-primary" />
                    {t('settings_page.objectifs', 'Objectifs')}
                  </CardTitle>
                  <CardDescription>
                    {t('settings_page.tes_objectifs_de_poids_et_nutrition', 'Tes objectifs de poids et nutrition')}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="current_weight">
                        {t('settings_page.poids_actuel', 'Poids actuel')} ({unitSystem === 'imperial' ? 'lb' : 'kg'})
                      </Label>
                      <Input
                        id="current_weight"
                        data-testid="settings-weight-input"
                        type="number"
                        step="0.1"
                        value={formData.current_weight}
                        onChange={(e) => setFormData({ ...formData, current_weight: e.target.value })}
                        className="bg-secondary border-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="target_weight">
                        {t('settings_page.poids_cible', 'Poids cible')} ({unitSystem === 'imperial' ? 'lb' : 'kg'})
                      </Label>
                      <Input
                        id="target_weight"
                        data-testid="settings-target-weight-input"
                        type="number"
                        step="0.1"
                        value={formData.target_weight}
                        onChange={(e) => setFormData({ ...formData, target_weight: e.target.value })}
                        className="bg-secondary border-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {unitSystem === 'metric' ? (
                      <div className="space-y-2">
                        <Label htmlFor="height">{t('settings_page.taille_cm', 'Taille (cm)')}</Label>
                        <Input
                          id="height"
                          data-testid="settings-height-input"
                          type="number"
                          value={formData.height_cm}
                          onChange={(e) => setFormData({ ...formData, height_cm: e.target.value })}
                          className="bg-secondary border-input"
                        />
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <Label>{t('settings_page.taille_pi_po', 'Taille (pi/po)')}</Label>
                        <div className="flex gap-2">
                          <Input
                            data-testid="settings-height-feet-input"
                            type="number"
                            placeholder={t('settings_page.pi', 'pi')}
                            value={formData.height_feet}
                            onChange={(e) => setFormData({ ...formData, height_feet: e.target.value })}
                            className="bg-secondary border-input w-20"
                          />
                          <Input
                            data-testid="settings-height-inches-input"
                            type="number"
                            placeholder={t('settings_page.po', 'po')}
                            value={formData.height_inches}
                            onChange={(e) => setFormData({ ...formData, height_inches: e.target.value })}
                            className="bg-secondary border-input w-20"
                          />
                        </div>
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label htmlFor="age">{t('settings_page.age', 'Âge')}</Label>
                      <Input
                        id="age"
                        data-testid="settings-age-input"
                        type="number"
                        value={formData.age}
                        onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                        className="bg-secondary border-input"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Activity Section */}
          {(
            <div
            >
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="font-heading text-xl uppercase tracking-tight flex items-center gap-2">
                    <Activity className="w-5 h-5 text-primary" />
                    {t('settings_page.niveau_d_activite', 'Niveau d\\\'activité')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="sex">{t('settings_page.sexe', 'Sexe')}</Label>
                      <Select
                        value={formData.sex}
                        onValueChange={(value) => setFormData({ ...formData, sex: value })}
                      >
                        <SelectTrigger id="sex" data-testid="settings-sex-select" className="bg-secondary border-input">
                          <SelectValue placeholder={t('settings_page.selectionner', 'Sélectionner')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="male">{t('settings_page.homme', 'Homme')}</SelectItem>
                          <SelectItem value="female">{t('settings_page.femme', 'Femme')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="activity">{t('settings_page.niveau_d_activite', 'Niveau d\\\'activité')}</Label>
                      <Select
                        value={formData.activity_level}
                        onValueChange={(value) => setFormData({ ...formData, activity_level: value })}
                      >
                        <SelectTrigger id="activity" data-testid="settings-activity-select" className="bg-secondary border-input">
                          <SelectValue placeholder={t('settings_page.selectionner', 'Sélectionner')} />
                        </SelectTrigger>
                        <SelectContent>
                          {(ACTIVITY_LEVELS[language] || ACTIVITY_LEVELS.fr).map((level) => (
                            <SelectItem key={level.value} value={level.value}>{level.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Job Activity Level — NEAT (Iter. 394).
                      Mirror of the IMC calculator field so existing users can edit
                      their NEAT profile from the main Settings form. Triggers a
                      backend macro recalc on save (see /users/profile PUT). */}
                  <div className="space-y-2" data-testid="settings-job-activity-level">
                    <Label htmlFor="job-activity" className="flex items-center gap-2">
                      <Briefcase className="w-4 h-4 text-orange-400" />
                      {language === 'fr' ? 'Profil professionnel (NEAT)' : language === 'es' ? 'Perfil profesional (NEAT)' : 'Job profile (NEAT)'}
                      {neatSettingsTip && (
                        <YumixTip
                          id={neatSettingsTip.id}
                          language={neatSettingsTip.language}
                          size="sm"
                          side="top"
                          title={neatSettingsTip.title}
                          text={neatSettingsTip.text}
                        />
                      )}
                    </Label>
                    <Select
                      value={formData.job_activity_level}
                      onValueChange={(value) => setFormData({ ...formData, job_activity_level: value })}
                    >
                      <SelectTrigger id="job-activity" data-testid="settings-job-activity-select" className="bg-secondary border-input">
                        <SelectValue placeholder={language === 'fr' ? 'Sédentaire (bureau)' : language === 'es' ? 'Oficina' : 'Desk job'} />
                      </SelectTrigger>
                      <SelectContent>
                        {(JOB_PROFILES[language] || JOB_PROFILES.fr).map((job) => (
                          <SelectItem
                            key={job.value}
                            value={job.value}
                            data-testid={`settings-job-activity-${job.value}`}
                          >
                            {job.label} <span className="text-muted-foreground ml-1">×{job.factor.toFixed(2)}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-muted-foreground">
                      {language === 'fr'
                        ? 'Tes macros seront recalculées à la sauvegarde selon ce facteur.'
                        : language === 'es'
                        ? 'Tus macros se recalcularán al guardar según este factor.'
                        : 'Macros are recalculated on save based on this factor.'}
                    </p>
                  </div>

                  {/* Calculated Targets Display */}
                  {user?.protein_min_g && user?.protein_max_g && (
                    <div className="bg-primary/10 border border-primary/30 p-4 rounded-lg mt-4">
                      <p className="text-sm text-muted-foreground mb-2">
                        {t('settings_page.tes_objectifs_mifflin_st_jeor_correction', 'Tes objectifs (Mifflin-St Jeor + correction -10%):')}
                      </p>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground">{t('settings_page.cible_proteines', 'Cible Protéines')}</p>
                          <p className="font-bold text-primary">
                            {safeNum(user.protein_min_g).toFixed(0)}-{safeNum(user.protein_max_g).toFixed(0)}g / {t('settings_page.jour', 'jour')}
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">{t('settings_page.budget_calories', 'Budget Calories')}</p>
                          <p className="font-bold text-foreground">
                            {safeNum(user.daily_calories).toFixed(0)} kcal / {t('settings_page.jour', 'jour')}
                          </p>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        {t('settings_page.maintien_500_kcal_de_deficit', '(Maintien - 500 kcal de déficit)')}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* IMC Calculator (extracted) */}
          <SettingsIMCCalculator />

          {/* Deficit Selector — Precision Metabolique */}
          <SettingsDeficitSelector />

          {/* Macro Targets (extracted) */}
          <SettingsMacroTargets />

          {/* Coach Booking Link Settings (for coach/admin only) */}
          {isPrivileged && (
            <Card className="bg-card border-border" data-testid="coach-booking-settings">
              <CardHeader>
                <CardTitle className="font-heading text-xl uppercase tracking-tight flex items-center gap-2">
                  <CalendarDays className="w-5 h-5 text-primary" />
                  {t('settings_page.lien_de_rendez_vous', 'Lien de Rendez-vous')}
                </CardTitle>
                <CardDescription>
                  {t('settings_page.ton_lien_calendly_ou_autre_sera_affiche_', 'Ton lien Calendly (ou autre) sera affiché à tes clients pour la prise de rendez-vous.')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label className="text-xs text-muted-foreground">
                    {t('settings_page.url_calendly_booking', 'URL Calendly / Booking')}
                  </Label>
                  <Input
                    value={formData.booking_link}
                    onChange={e => setFormData(prev => ({...prev, booking_link: e.target.value}))}
                    placeholder="https://calendly.com/ton-lien/30min"
                    className="mt-1"
                    data-testid="booking-link-input"
                  />
                  <p className="text-[10px] text-muted-foreground/60 mt-1">
                    {t('settings_page.ce_lien_sera_inclus_dans_les_alertes_de_', 'Ce lien sera inclus dans les alertes de fin de plan et visible par tes clients.')}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Book Appointment Section (for clients) */}
          {!isPrivileged && (
            <Card className="bg-gradient-to-r from-primary/10 to-card border-primary/30">
              <CardHeader>
                <CardTitle className="font-heading text-xl uppercase tracking-tight flex items-center gap-2">
                  <img src="/mascot-yuri.png" alt="Coach" className="w-8 h-8 object-contain" />
                  {coachBookingName}
                </CardTitle>
                <CardDescription>
                  {t('settings_page.book_with_coach', "Besoin d'aide personnalisée ? Prends rendez-vous avec {{name}} !", { name: coachBookingName })}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {coachBookingLink ? (
                  <a
                    href={coachBookingLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-4 py-3 rounded-lg transition-colors"
                    data-testid="settings-book-appointment-btn"
                  >
                    <CalendarDays className="w-5 h-5" />
                    {t('settings_page.prendre_rendez_vous', 'Prendre rendez-vous')}
                  </a>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-2">
                    {t('settings_page.contacte_ton_coach_via_la_messagerie_pou', 'Contacte ton coach via la messagerie pour prendre rendez-vous.')}
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Submit Button */}
          <div
          >
            <Button
              type="submit"
              data-testid="settings-save-btn"
              disabled={loading}
              className="w-full btn-skewed bg-primary text-primary-foreground hover:bg-primary/90 font-heading uppercase tracking-wider text-lg py-4 h-auto"
            >
              <span className="flex items-center justify-center gap-2">
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {t('common.loading')}
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    {t('settings.save')}
                  </>
                )}
              </span>
            </Button>
          </div>
        </form>

        {/* Session Info Section */}
        <div className="mt-8">
          <Card className="bg-card border-primary/30">
            <CardHeader>
              <CardTitle className="font-heading text-xl uppercase tracking-tight flex items-center gap-2">
                <Shield className="w-5 h-5 text-primary" />
                {t('settings_page.session_active', 'Session Active')}
              </CardTitle>
              <CardDescription>
                {t('settings_page.informations_sur_votre_connexion_actuell', 'Informations sur votre connexion actuelle')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {sessionLoading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : sessionInfo ? (
                <div className="space-y-4">
                  {/* Session Status */}
                  <div className="flex items-center justify-between p-4 bg-secondary/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${sessionInfo.is_valid ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
                      <div>
                        <p className="font-medium text-foreground">
                          {t('settings_page.statut', 'Statut')}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {sessionInfo.is_valid 
                            ? (t('settings_page.connecte_et_securise', 'Connecté et sécurisé'))
                            : (t('settings_page.session_expiree', 'Session expirée'))}
                        </p>
                      </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      sessionInfo.is_valid ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                    }`}>
                      {sessionInfo.is_valid 
                        ? (t('settings_page.actif', 'Actif'))
                        : (t('settings_page.expire', 'Expiré'))}
                    </span>
                  </div>
                  
                  {/* Expiration Info */}
                  <div className="flex items-center justify-between p-4 bg-secondary/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <CalendarDays className="w-5 h-5 text-primary" />
                      <div>
                        <p className="font-medium text-foreground">
                          {t('settings_page.expiration', 'Expiration')}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {sessionInfo.expires_at 
                            ? new Date(sessionInfo.expires_at).toLocaleDateString(t('settings_page.fr_ca', 'fr-CA'), {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })
                            : 'N/A'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`text-2xl font-bold ${
                        sessionInfo.days_remaining > 7 ? 'text-green-400' : 
                        sessionInfo.days_remaining > 3 ? 'text-yellow-400' : 'text-red-400'
                      }`}>
                        {sessionInfo.days_remaining}
                      </span>
                      <p className="text-xs text-muted-foreground">
                        {t('settings_page.jours_restants', 'jours restants')}
                      </p>
                    </div>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">
                        {t('settings_page.duree_de_la_session', 'Durée de la session')}
                      </span>
                      <span className="text-foreground font-medium">
                        {sessionInfo.session_duration_days} {t('settings_page.jours', 'jours')}
                      </span>
                    </div>
                    <Progress 
                      value={(sessionInfo.days_remaining / sessionInfo.session_duration_days) * 100} 
                      className="h-2"
                    />
                  </div>
                  
                  {/* Info Note */}
                  <p className="text-xs text-muted-foreground text-center pt-2">
                    {t('settings_page.votre_session_sera_automatiquement_renou', 'Votre session sera automatiquement renouvelée lors de votre prochaine connexion.')}
                  </p>
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-4">
                  {t('settings_page.impossible_de_charger_les_informations_d', 'Impossible de charger les informations de session')}
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Google Calendar (extracted) */}
        <SettingsCalendar />
        {/* Clinical Mode - accessible to ALL users */}
        <div className="mt-8">
          <ClinicalModeSettings />
        </div>
        {/* Injury & Mood (extracted) */}
        <SettingsInjuryMood />
        <div className="mt-8"><RecoveryJournal /></div>

        {/* Communication Systems Test Section (Coach/Admin only) */}
        {isCoachOrAdmin && (
          <div className="mt-8" data-testid="comm-test-section">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="font-heading text-xl uppercase tracking-tight flex items-center gap-2">
                  <Send className="w-5 h-5 text-primary" />
                  {t('settings_page.test_des_communications', 'Test des Communications')}
                </CardTitle>
                <CardDescription>
                  {t('settings_page.verifie_que_les_systemes_email_et_push_s', 'Verifie que les systèmes email et push sont operationnels avant lundi.')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Infrastructure Status */}
                <CommTestPanel language={language} />
              </CardContent>
            </Card>
          </div>
        )}

        {/* Data Management (extracted) */}
        <SettingsDataManagement />

        {/* Legal section — Zéro Relais / Global Deployment */}
        <div className="mt-8" data-testid="settings-legal-section">
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="font-heading text-xl uppercase tracking-tight flex items-center gap-2">
                <Scale className="w-5 h-5 text-primary" />
                {t('settings_page.legal', 'Legal')}
              </CardTitle>
              <CardDescription>
                {t('settings_page.documentation_juridique_complete_cgu_ave', 'Documentation juridique complete : CGU (avec clause de non-divulgation), Politique de Confidentialite et Avis de Non-Responsabilite Medicale.')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <a
                href={LEGAL_DOC_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between gap-3 p-4 rounded-xl border border-border/70 bg-secondary/30 hover:bg-secondary/60 transition group min-w-0 w-full"
                data-testid="settings-legal-doc-link"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="p-2.5 bg-primary/10 rounded-lg shrink-0">
                    <FileText className="w-5 h-5 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-foreground text-sm">
                      {t('settings_page.documentation_legale', 'Documentation Legale')}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {t('settings_page.cgu_confidentialite_non_responsabilite_m', 'CGU, Confidentialite, Non-Responsabilite Medicale, Non-divulgation')}
                    </div>
                  </div>
                </div>
                <ExternalLink className="w-4 h-4 text-muted-foreground group-hover:text-primary shrink-0" />
              </a>

              {user?.legal_consent_version && (
                <div className="flex items-start gap-2 text-xs text-muted-foreground pt-1">
                  <CheckCircle className="w-3.5 h-3.5 text-emerald-500 mt-0.5 shrink-0" />
                  <span>
                    {t('settings_page.terms_version_notice', 'Conditions acceptées (version {{user_version}}). Version en vigueur : {{current_version}}.', { user_version: user.legal_consent_version, current_version: LEGAL_CONSENT_VERSION })}
                  </span>
                </div>
              )}

              {/* 🌍 Compliance notice — discreet, only outside CA/US. */}
              {needsComplianceNotice(user?.country) && (
                <div
                  className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mt-2"
                  data-testid="settings-compliance-notice"
                >
                  <Globe className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span className="leading-relaxed">
                    {t('settings_page.compliance_notice', 'Avis de conformité : tes calculs nutritionnels reposent sur les standards nord-américains (FCEN + USDA) et les recommandations de l\'OMS. Pays détecté : {{country}}. Pour plus de précision locale, consulte les lignes directrices de ton pays.', { country: user.country })}
                  </span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Profile Transition Animation */}
      <ProfileTransition 
        profile={pendingProfile} 
        visible={showProfileTransition} 
        onComplete={handleProfileTransitionComplete} 
      />
    </Layout>
  );
};

export default Settings;
